import { PayinIcon } from "@images/svgIcons/payinIcon"
import { PayoutIcon } from "@images/svgIcons/payoutIcon"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { FinanceCard } from "@modules/finances/components/financeCard"
import { FinanceTotalMonthCard } from "@modules/finances/components/financeTotalMonthCard"
import { LastUpdateComponent } from "@modules/finances/components/lastUpdateInformation"
import { StripeAccountErrorAlert } from "@modules/finances/components/stripeAccountErrorAlert"
import { StripeStatusBanner } from "@modules/finances/components/stripeStatusBanner"
import { UserTransactionsTable } from "@modules/finances/components/userTransactionsTable"
import { useFinanceStats } from "@modules/finances/hooks/useFinance"
import { usePeriodParam } from "@modules/finances/hooks/usePeriod"
import { useStripeAccount } from "@modules/finances/hooks/useStripeAccount"
import { UserPayoutLayout } from "@modules/financesPayout/components/userPayoutLayout"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { usePrograms } from "@modules/programs/hooks/usePrograms"
import { usePromoCodeSidebar } from "@modules/promoCode/hooks/usePromoCodeSidebar"
import { ScreenHeader } from "@modules/ui/components/header"
import { useToast } from "@modules/ui/components/huToast"
import { HURow, HUStack } from "@modules/ui/components/layout"
import { MenuItem } from "primereact/menuitem"
import { TabMenu } from "primereact/tabmenu"
import { useMemo, useState } from "react"
import { useTheme } from "styled-components"

export const UserFinancesPage: React.FC = () => {
  const language = useLanguage()
  const theme = useTheme()
  const {
    createStripeAccount,
    updateStripeAccount,
    loading: actionLoading,
    useStripeAccountStatus,
  } = useStripeAccount()

  const [period] = usePeriodParam()
  const { loading: stripeAccountStatusLoading, stripeAccountStatus } = useStripeAccountStatus()
  const { navigateToPromoCodeListView } = usePromoCodeSidebar()
  const { result: programs } = usePrograms({ programFilter: "OWNED", onlyWithPrice: true, size: 1 })
  const toast = useToast()
  const isStripeAccountComplete = stripeAccountStatus === "OK"

  const [menuTab, setMenuTab] = useState(0)
  const items: MenuItem[] = [
    {
      label: word("finances.page.menu.sales"),
      icon: <PayinIcon color={menuTab === 0 ? theme.blackAtheleWhiteOwner : theme.neutralColor300} className="mr-1" />,
    },
    {
      label: word("finances.page.menu.payouts"),
      icon: <PayoutIcon color={menuTab === 1 ? theme.blackAtheleWhiteOwner : theme.neutralColor300} className="mr-1" />,
    },
  ]

  const {
    data: stats,
    isLoading: isLoadingStats,
    isRefetching,
    updateStats,
  } = useFinanceStats(isStripeAccountComplete, period)

  const cardLoading = isLoadingStats || stripeAccountStatusLoading

  const openStripeAccount = () => {
    if (stripeAccountStatus === "OK" || stripeAccountStatus === "REQUIRE_ADDITIONAL_INFORMATION") updateStripeAccount()
    else createStripeAccount()
  }

  const openPromoCodeSidebar = () => {
    if (programs.length > 0) navigateToPromoCodeListView()
    else toast.show({ detail: word("promoCode.toast.missingProgram"), severity: "warn" })
  }

  const renderFinanceTotalMonthCard = useMemo(() => {
    return (
      <FinanceTotalMonthCard
        cardType="Primary"
        cardTitle={word("finances.page.card.ca.label")}
        value={stats ? formatCurrency(stats.totalTransactionsValue, stats.totalTransactionsCurrency, language) : "--"}
        loading={cardLoading}
        period={period}
        className="flex-grow-1 overflow-hidden rounded-33"
      />
    )
  }, [stats, cardLoading, period, language])

  if (!isStripeAccountComplete)
    return (
      <div className="flex flex-column flex-grow-1">
        <ScreenHeader title={word("menu.finances")} />
        <div className="flex">
          <StripeStatusBanner />
        </div>
      </div>
    )
  else
    return (
      <div className="flex flex-column flex-grow-1" style={{ width: "100%" }}>
        <ScreenHeader title={word("menu.finances")}>
          {menuTab === 0 ? (
            <LastUpdateComponent isRefetchStats={isRefetching} updateStats={updateStats} />
          ) : (
            <div className="mt-6" />
          )}
        </ScreenHeader>
        <div className="flex w-full">
          <TabMenu model={items} activeIndex={menuTab} onTabChange={(e) => setMenuTab(e.index)} className="w-full" />
        </div>
        {menuTab === 0 && (
          <HUStack gap={28} className="flex-grow-1" style={{ minWidth: 1000 }}>
            <StripeAccountErrorAlert orientation="horizontal" />
            <HURow gap={28}>
              {renderFinanceTotalMonthCard}
              <FinanceCard
                cardType="Secondary"
                cardTitle={word("finances.page.card.promoCodes.label")}
                cardSubtitle={word("finances.page.card.promoCodes.description")}
                value={false}
                loading={cardLoading}
                buttonLabel={word("finances.page.card.promoCodes.button")}
                isButtonLoading={false}
                onButtonClick={openPromoCodeSidebar}
                className="flex-1 overflow-hidden rounded-33"
              />
              <FinanceCard
                cardType="Secondary"
                cardTitle={word("finances.page.card.myPaymentInformation")}
                loading={cardLoading}
                value={false}
                icon="pi pi-wallet"
                buttonLabel={word("finances.page.card.seeMyPaymentInformation")}
                buttonIcon="pi pi-arrow-right"
                isButtonLoading={actionLoading}
                onButtonClick={openStripeAccount}
                className="flex-1 overflow-hidden rounded-33"
              />
            </HURow>
            <UserTransactionsTable />
          </HUStack>
        )}
        {menuTab === 1 && <UserPayoutLayout />}
      </div>
    )
}
